import { useState, useEffect } from 'preact/hooks';
import { ReferralTableSkeleton } from './LoadingSkeletons';
import { t } from '../i18n';
import { getConfig } from '../config';
import { getReferralTree } from '../api';
import { navigateToView } from '../navigation';
import { formatPrice } from '../utils/currency';

interface ReferralRow {
  firstname?: string;
  lastname?: string;
  level?: number;
  sales?: string | number;
  child_sales?: string | number;
}

interface ReferralTree {
  parent_referral: { name?: string; level?: number; sales?: string | number; child_sales?: string | number };
  referrals: ReferralRow[];
}

function truncateName(s: string | undefined): string {
  if (!s) return '';
  return s.length > 4 ? s.slice(0, 4) + '***' : s;
}

export function ReferralPage() {
  const config = getConfig();
  const [data, setData] = useState<ReferralTree | null>(null);
  const [loading, setLoading] = useState(true);

  const backButton = (
    <button type="button" class="btn btn-outline-secondary btn-sm referral-card__back-btn" onClick={() => navigateToView('bonus-account')}>
      <span style="display:inline-flex;align-items:center;gap:.35rem">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {t('referral.back')}
      </span>
    </button>
  );

  useEffect(() => {
    getReferralTree(config).then((res) => {
      setLoading(false);
      if (res.status === 200 && res.result) setData(res.result as ReferralTree);
    });
  }, []);

  if (loading) {
    return <ReferralTableSkeleton />;
  }

  if (!data?.parent_referral) {
    return (
      <div class="sw-tickets-shell referral-shell">
        <div class="shop-widget-referral-page sw-surface-card referral-card">
          {backButton}
          <div class="referral-card__empty">{t('referral.empty')}</div>
        </div>
      </div>
    );
  }

  const parent = data.parent_referral;
  const referrals = data.referrals || [];

  return (
    <div class="sw-tickets-shell referral-shell">
      <div class="shop-widget-referral-page sw-surface-card referral-card">
        {backButton}
        <p class="referral-card__description mb-0">{t('referral.description')}</p>

        <div class="table-responsive referral-table-wrap">
          <table class="table referral-table">
            <thead>
              <tr>
                <th style={{ width: '56%' }}>{t('referral.referral')}</th>
                <th style={{ whiteSpace: 'nowrap' }}>{t('referral.bonuses')}</th>
                <th style={{ whiteSpace: 'nowrap' }}>{t('referral.subBonuses')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{parent.name} ({t('referral.level')} {parent.level ?? '-'})</td>
                <td class="referral-table__sales">{formatPrice(parent.sales)}</td>
                <td class="referral-table__child-sales">{formatPrice(parent.child_sales)}</td>
              </tr>
              {referrals.map((v, k) => {
                const levelDelta = (parent.level != null && v.level != null) ? v.level - parent.level + 1 : 0;
                const strong = levelDelta <= 3;
                const name = `${truncateName(v.firstname)} ${truncateName(v.lastname)}`.trim() || '-';
                return (
                  <tr key={k}>
                    <td>
                      {strong ? <strong>{name} ({t('referral.level')} {v.level})</strong> : <>{name} ({t('referral.level')} {v.level})</>}
                    </td>
                    <td class="referral-table__sales">{formatPrice(v.sales)}</td>
                    <td class="referral-table__child-sales">{formatPrice(v.child_sales)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div class="referral-mobile-list">
          <div class="referral-mobile-list__card">
            <div class="referral-mobile-list__row">
              <span>{t('referral.referral')}</span>
              <strong>{parent.name} ({t('referral.level')} {parent.level ?? '-'})</strong>
            </div>
            <div class="referral-mobile-list__row">
              <span>{t('referral.bonuses')}</span>
              <strong class="referral-table__sales">{formatPrice(parent.sales)}</strong>
            </div>
            <div class="referral-mobile-list__row">
              <span>{t('referral.subBonuses')}</span>
              <strong class="referral-table__child-sales">{formatPrice(parent.child_sales)}</strong>
            </div>
          </div>
          {referrals.map((v, k) => {
            const levelDelta = (parent.level != null && v.level != null) ? v.level - parent.level + 1 : 0;
            const strong = levelDelta <= 3;
            const name = `${truncateName(v.firstname)} ${truncateName(v.lastname)}`.trim() || '-';
            return (
              <div class="referral-mobile-list__card" key={`m-${k}`}>
                <div class="referral-mobile-list__row">
                  <span>{t('referral.referral')}</span>
                  <strong class={strong ? 'fw-bold' : ''}>{name} ({t('referral.level')} {v.level})</strong>
                </div>
                <div class="referral-mobile-list__row">
                  <span>{t('referral.bonuses')}</span>
                  <strong class="referral-table__sales">{formatPrice(v.sales)}</strong>
                </div>
                <div class="referral-mobile-list__row">
                  <span>{t('referral.subBonuses')}</span>
                  <strong class="referral-table__child-sales">{formatPrice(v.child_sales)}</strong>
                </div>
              </div>
            );
          })}
        </div>

        <div class="referral-card__notes">
          <p class="small text-muted mb-1">{t('referral.bonusesNote')}</p>
          <p class="small text-muted mb-0">{t('referral.subBonusesNote')}</p>
        </div>
      </div>
    </div>
  );
}
